import React, { memo, useState, useId, useMemo } from 'react';
import { useDataProvider } from 'react-admin';
import { useTranslation } from 'react-i18next';
import { Box, Input, Button, LinearProgress, FormControl } from '@mui/material';
import { SxProps } from '@mui/system';
import { ImageCrop, ImageCropRef, ImageCropProps } from '../ImageCrop/ImageCrop';
import { getBaseCleanUrl, ImageData } from './ImageData';
import { commonPreviewPayload } from 'providers/contexts/extensions/functions/formatters';
import { BloxCustomDataProviderType } from 'utility/types/dataProvider';
import { mergeDeep } from 'remeda';
import { acceptImageMimeObject } from 'utility/constants';

/**
 * A component for uploading images.
 *
 * NOTE: This component should be deprecated in favor of the new FileFieldInput component.
 *
 * @component
 * @param {ImageData} imgDataState - SyncedStore or State img Data
 * @param {function} setImgDataCb -  Callback function to set image data state (ImageData + ImgActionType).
 * @param {string} initialButtonText - The text to display on the upload button when no image is selected.
 * @param {string} populatedButtonText - The text to display on the upload button when an image is in place.
 * @param {boolean} showRemove - Whether to show the remove button.
 * @param {string} cropSource - The source for the crop data in the payload. Also used to include crop options
 * @param {SxProps} sx - The style props for the component.
 * @param {boolean} fullWidth - Whether the component should take up the full width.
 * @param {boolean} disabled - Whether the component should be disabled.
 * @param {ImageCropRef} cropperRef - Optional ref to the ImageCrop component
 * @param {boolean} keepCropMounted - Optional control ImageCrop dialogs keepMounted
 * @param {function} onCropperReady - Optional callback when the cropper emits ready
 * @returns {React.Component} The rendered ImageUpload component.
 */

export type ImgActionType = 'upload' | 'removed' | 'cropped';

export type ImageUploadProps = {
  imgDataState: ImageData;
  setImgDataCb: (data: ImageData, action: ImgActionType) => void;
  initialButtonText?: string;
  populatedButtonText?: string;
  showRemove?: boolean;
  sx?: SxProps;
  fullWidth?: boolean;
  disabled?: boolean; // eg read only mode
  cropperRef?: ImageCropRef;
  keepCropMounted?: ImageCropProps['keepCropMounted'];
  onCropperReady?: ImageCropProps['onCropperReady'];
  showCrop?: boolean;
};

export const ImageUpload = memo(
  ({
    imgDataState,
    setImgDataCb,
    initialButtonText,
    populatedButtonText,
    showRemove,
    sx,
    fullWidth,
    disabled,
    cropperRef,
    onCropperReady,
    keepCropMounted = false,
    showCrop = true,
  }: ImageUploadProps) => {
    const { t } = useTranslation();
    const inputId = useId();
    const strUpload = t('blox.actions.upload');
    const strRemove = t('blox.actions.remove');
    const strChange = t('blox.actions.change');

    const [loading, setLoading] = useState(false);
    const dataProvider = useDataProvider<BloxCustomDataProviderType>();

    const buttonText = useMemo(() => {
      return imgDataState?.preview
        ? (populatedButtonText ?? strChange)
        : (initialButtonText ?? strUpload);
    }, [imgDataState, populatedButtonText, initialButtonText, strChange, strUpload]);

    const showCropBtn = showCrop && imgDataState?.src;

    const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event?.target?.files?.[0];

      if (!file) {
        return;
      }

      try {
        // Set loading
        setLoading(true);

        // Upload the file using the binary upload process
        const binaryUpload = await dataProvider.binaryUpload({
          file: file,
          preview: commonPreviewPayload?.preview,
        });

        const imgDisplayUrl = binaryUpload?.data?.previews?.display?.url;
        // clean cdn url without any crop or resize params
        const imgBaseUrl = getBaseCleanUrl(imgDisplayUrl) ?? '';
        const imageWidth = binaryUpload?.data?.width;
        const imageHeight = binaryUpload?.data?.height;
        const imageSize = binaryUpload?.data?.size;
        const imageType = binaryUpload?.data?.type;
        // previews objects
        const imagePreviews = binaryUpload?.data?.previews;
        // new binary id
        const addBinaryId = binaryUpload?.data?.id;

        // Note: the width/height must match the actual
        // dimensions of the cdn image being set or else
        // cropped saves may get 'out of range' errors..
        // the file blob may not always match the api dimensions
        // so ensure src is the url from the upload response, not local blob.

        // Update state image data and reset crops
        setImgDataCb(
          {
            remove_binary: undefined, // clear any pending removal
            add_binary: addBinaryId, // new binary id
            crop: null, // reset crop on fresh upload
            src: imgBaseUrl,
            preview: imgBaseUrl,
            height: imageHeight,
            width: imageWidth,
            size: imageSize,
            type: imageType,
            previews: imagePreviews,
          },
          'upload',
        );
      } catch (error) {
        console.error('Error uploading file:', error);
      } finally {
        // Set loading false after upload is complete
        setLoading(false);
      }
    };

    const handleRemoveImage = () => {
      setImgDataCb(
        {
          remove_binary: true,
          add_binary: null, // clear any add binary
          crop: null,
          preview: undefined,
          src: undefined,
          height: undefined,
          width: undefined,
          size: undefined,
          type: undefined,
          previews: undefined,
        },
        'removed',
      );
    };

    const handleImgCropUpdate = (updatedImgData: ImageData) => {
      // merge updated crop data with existing img data state
      const newState = mergeDeep(imgDataState, updatedImgData);
      setImgDataCb(newState, 'cropped');
    };

    return (
      <FormControl
        data-testid="image-upload-form-control"
        fullWidth={fullWidth}
        disabled={disabled}
        sx={{
          ...sx,
          '& .MuiInputBase-root': {
            display: 'none', // Hide the default InputBase
          },
        }}
      >
        <Input
          data-testid="image-upload-input"
          type="file"
          onChange={handleImageChange}
          inputProps={{
            accept: Object.keys(acceptImageMimeObject),
            id: `image-upload-input-${inputId}`,
            style: { display: 'none' },
          }}
        />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            mb: 0.75,
          }}
        >
          <Button
            data-testid="image-upload-button"
            variant="contained"
            component="label"
            htmlFor={`image-upload-input-${inputId}`}
            disabled={disabled}
            aria-disabled={disabled ? 'true' : 'false'}
            sx={{
              flexGrow: 1,
              marginRight: 0.5,
            }}
          >
            {buttonText}
          </Button>
          {showCropBtn && (
            <ImageCrop
              ref={cropperRef}
              keepCropMounted={keepCropMounted}
              imgData={imgDataState}
              onImgDataUpdate={handleImgCropUpdate}
              onCropperReady={onCropperReady}
              triggerButtonProps={{
                disabled: disabled,
              }}
            />
          )}
        </Box>
        {loading && <LinearProgress />}
        {imgDataState?.preview && !loading && (
          <div>
            <img
              data-testid="image-upload-preview"
              alt="Preview"
              src={imgDataState?.preview}
            />
            {showRemove && (
              <Button
                data-testid="image-remove-button"
                fullWidth
                variant="outlined"
                onClick={handleRemoveImage}
                color="error"
                disabled={disabled}
              >
                {strRemove}
              </Button>
            )}
          </div>
        )}
      </FormControl>
    );
  },
);
